import { useNavigate } from "react-router-dom";
import type { BoutiqueCardDTO, DashboardDTO } from "@easyshop/shared";
import { Card, Empty, ErrorState, Loading, TypeBadge, cx } from "@/components/ui";
import { PeriodBar } from "@/components/PeriodBar";
import { BoutiqueTile, CaPanel, KpiTiles } from "@/components/pc/Kit";
import { TopBoutiquesGroup, TopProductsGroup } from "@/components/Rankings";
import { IosSection } from "@/components/mobile/MobileUi";
import { useSession } from "@/hooks/useSession";
import { useUi } from "@/hooks/useUi";
import { useDashboard } from "@/lib/queries";
import { PageStack } from "@/layouts/DesktopShell";
import { BoutiquePage } from "./Boutique";
import { eur, margin, num, pct1 } from "@/lib/format";

/**
 * Accueil PC. Propriétaire et gérant multi-boutiques (sur son périmètre) :
 * carte CA + KPI, classements, cartes boutiques. Gérant d'une seule boutique :
 * l'aperçu de sa boutique.
 */
export function DashboardPage() {
  const { me } = useSession();
  const single = !me.networkWide && me.boutiques.length === 1 ? me.boutiques[0] : null;
  if (single) return <BoutiquePage boutiqueId={single.id} />;
  return <NetworkHome />;
}

function NetworkHome() {
  const { me } = useSession();
  const q = useDashboard();
  const villes = me.boutiques.map((b) => b.city);
  return (
    <PageStack>
      {/* Pas de grand titre « Accueil » : la navigation l'indique déjà. */}
      <h1 className="sr-only">Accueil</h1>
      <div className="truncate text-[15px] text-muted">
        {me.networkWide ? `Réseau · ${me.networkSize} boutiques` : `Mes boutiques · ${villes.join(", ")}`}
      </div>
      <PeriodBar />
      {q.isLoading ? <Loading /> : q.error ? <ErrorState error={q.error} /> : q.data ? <HomeContent data={q.data} /> : null}
    </PageStack>
  );
}

function HomeContent({ data: d }: { data: DashboardDTO }) {
  const { me } = useSession();
  const navigate = useNavigate();
  const { openProduct } = useUi();
  const k = d.kpis;
  const open = (id: string) => navigate(`/boutiques/${id}`);
  const prev = d.boutiques.filter((b) => b.prevCa != null);
  const prevCa = prev.length ? prev.reduce((s, b) => s + (b.prevCa ?? 0), 0) : null;
  const maxCa = Math.max(1, ...d.boutiques.map((b) => b.ca));
  const ranked = [...d.boutiques].sort((a, b) => b.ca - a.ca);

  return (
    <>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <CaPanel
          label={`${me.networkWide ? "CA du réseau" : "CA de mes boutiques"} — ${d.period.title}`}
          ca={k.ca}
          delta={k.delta}
          prevCa={prevCa}
          columns={d.cmpColumns}
          chart={d.chart}
          split={me.networkWide ? { integrated: k.caIntegrated, franchise: k.caFranchise } : undefined}
          segments={me.networkWide ? { integrated: d.cmpColumnsIntegrated, franchise: d.cmpColumnsFranchise } : undefined}
        />
        <KpiTiles
          columns={d.cmpColumns}
          items={[
            ["Tickets", num(k.tickets), (c) => c.tickets],
            ["Panier moyen", eur(k.panier), (c) => c.panier],
            ["Marge brute", margin(k.marge), (c) => c.marge],
            ["% objectif", k.objectivePct == null ? "—" : `${Math.round(k.objectivePct)} %`, (c) => c.objectif],
          ]}
        />
      </div>

      <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
        {me.networkWide ? (
          <>
            <TopBoutiquesGroup title="Top succursales intégrées" items={d.topIntegrated} max={maxCa} color="bg-positive" onSelect={open} />
            <TopBoutiquesGroup title="Top franchises" items={d.topFranchise} max={maxCa} color="bg-franchise" onSelect={open} />
          </>
        ) : (
          <div className="xl:col-span-2">
            <TopBoutiquesGroup title="Classement de mes boutiques" items={ranked.slice(0, 8)} max={maxCa} color="bg-positive" onSelect={open} />
          </div>
        )}
        <TopProductsGroup
          title="Top produits"
          products={d.topProducts}
          ranked
          onSelect={(p) => openProduct({ id: p.id, name: p.name, scope: "reseau" })}
        />
      </div>

      <IosSection title={me.networkWide ? "Boutiques" : "Mes boutiques"}>
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(210px,1fr))]">
          {ranked.map((b) => (
            <BoutiqueTile key={b.id} b={b} onClick={() => open(b.id)} />
          ))}
        </div>
      </IosSection>
    </>
  );
}

// --- Cartes boutiques (partagées avec la page « Boutiques ») ----------------------------

export function BoutiqueCard({ b, onClick }: { b: BoutiqueCardDTO; onClick?: () => void }) {
  const up = (b.delta ?? 0) >= 0;
  return (
    <div
      onClick={onClick}
      className={cx("relative cursor-pointer rounded-card-sm border border-line bg-white px-[18px] py-4 transition-colors hover:border-[oklch(0.82_0.02_150)]")}
    >
      {(b.openTasks > 0 || b.unread > 0) && (
        <div className="absolute right-3 top-3 flex gap-1">
          {b.openTasks > 0 && (
            <span title="Tâches ouvertes" className="rounded-full bg-warn-bg px-[6px] py-px text-[10px] font-bold text-warn">
              {b.openTasks}
            </span>
          )}
          {b.unread > 0 && (
            <span title="Messages non lus" className="rounded-full bg-accent px-[6px] py-px text-[10px] font-bold text-white">
              {b.unread}
            </span>
          )}
        </div>
      )}
      <div className="truncate pr-12 text-[14px] font-bold">{b.city}</div>
      <div className="mt-[5px] flex min-w-0 items-center gap-[6px]">
        <TypeBadge type={b.type} synced={b.synced} />
        {b.promosToday.length > 0 && (
          <span
            title={`Promo du jour : ${b.promosToday.map((p) => p.name).join(", ")}`}
            className="min-w-0 truncate rounded-full bg-accent px-[8px] py-[2px] text-[10px] font-bold text-white"
          >
            🏷 {b.promosToday.length === 1 ? b.promosToday[0].name : `${b.promosToday.length} promos`}
          </span>
        )}
      </div>
      <div className="mt-[10px] font-display text-[21px] font-extrabold tabular">{eur(b.ca)}</div>
      <div className={cx("mt-[2px] text-[12px] font-semibold", b.delta == null ? "text-faint" : up ? "text-positive" : "text-danger")}>
        {b.delta == null ? "—" : `${up ? "▲" : "▼"} ${pct1(b.delta)}`}
      </div>
      {b.prevCa != null && (
        <div className="mt-[2px] text-[11.5px] text-muted-2">
          Avant : <span className="font-semibold tabular">{eur(b.prevCa)}</span>{" "}
          <span className={cx("font-bold tabular", b.ca - b.prevCa >= 0 ? "text-positive" : "text-danger")}>
            ({b.ca - b.prevCa >= 0 ? "+" : "−"}
            {eur(Math.abs(b.ca - b.prevCa))})
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * « Top boutique » de la période sélectionnée, en chiffres seulement : CA de la
 * meilleure boutique et de la plus basse, pour les succursales intégrées et pour
 * les franchises. Les boutiques non synchronisées sont ignorées (leur CA à 0
 * fausserait le bas du classement).
 */
export function BestWorstCard({ boutiques, className }: { boutiques: BoutiqueCardDTO[]; className?: string }) {
  const groups = [
    { label: "Intégrée", type: "INTEGRATED", dot: "bg-positive" },
    { label: "Franchise", type: "FRANCHISE", dot: "bg-franchise" },
  ] as const;
  const rows = groups.map((g) => {
    const list = boutiques.filter((b) => b.type === g.type && b.synced).sort((a, b) => b.ca - a.ca);
    return { ...g, best: list[0], worst: list.length > 1 ? list[list.length - 1] : undefined };
  });
  // Un seul type de boutique dans le périmètre (ex. que des franchises) : pas de distinction à afficher.
  const shown = rows.filter((r) => r.best);
  if (shown.length === 0) return null;
  const single = shown.length === 1;
  return (
    <Card className={cx("min-w-0", className)} pad="px-[18px] py-[14px]">
      <div className="truncate text-[12px] font-semibold text-muted">Top boutique</div>
      <div className="mt-2 flex flex-col gap-[10px]">
        {shown.map((r) => (
          <div key={r.type} className="min-w-0">
            {!single && (
              <div className="mb-[2px] flex items-center gap-[6px] text-[11px] font-semibold text-muted-2">
                <span className={cx("h-2 w-2 flex-shrink-0 rounded-[3px]", r.dot)} />
                {r.label}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase text-positive">Meilleure</div>
                <div className="truncate font-display text-[16px] font-extrabold tabular">{r.best ? eur(r.best.ca) : "—"}</div>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase text-danger">Plus basse</div>
                <div className="truncate font-display text-[16px] font-extrabold tabular">{r.worst ? eur(r.worst.ca) : "—"}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Grille auto-fill (minmax 210 px) des cartes boutiques ; clic → fiche boutique. */
export function BoutiqueGrid({ boutiques }: { boutiques: BoutiqueCardDTO[] }) {
  const navigate = useNavigate();
  if (boutiques.length === 0) return <Empty>Aucune boutique dans votre périmètre.</Empty>;
  // Du plus gros CA au plus bas de la période sélectionnée.
  const sorted = [...boutiques].sort((x, y) => y.ca - x.ca || x.city.localeCompare(y.city, "fr"));
  return (
    <div className="grid gap-[14px] [grid-template-columns:repeat(auto-fill,minmax(210px,1fr))]">
      {sorted.map((b) => (
        <BoutiqueCard key={b.id} b={b} onClick={() => navigate(`/boutiques/${b.id}`)} />
      ))}
    </div>
  );
}
